import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCart } from '@/hooks/useCart';
import { useDeliveryLocation } from '@/contexts/DeliveryLocationContext';

export default function CartCityValidator() {
  const { state: cartState, removeUnavailableItems } = useCart();
  const { selectedCity } = useDeliveryLocation();

  // Fetch product availability data for cart items
  const { data: productAvailability } = useQuery({
    queryKey: ['cart-city-validation', cartState.items.map(item => item.id), selectedCity?.slug],
    queryFn: async () => {
      if (!selectedCity || cartState.items.length === 0) return {};
      
      const { data, error } = await supabase
        .from('products')
        .select(`
          id,
          slug,
          product_delivery_locations (city)
        `)
        .in('id', cartState.items.map(item => item.id));
      
      if (error) throw error;
      
      // Create a map of product availability
      const availabilityMap: Record<string, boolean> = {};
      data?.forEach(product => {
        if (product.product_delivery_locations && product.product_delivery_locations.length > 0) {
          availabilityMap[product.id] = product.product_delivery_locations.some(
            (loc: any) => loc.city === selectedCity.slug
          );
        } else {
          // If no delivery locations specified, assume available everywhere
          availabilityMap[product.id] = true;
        }
      });
      
      return availabilityMap;
    },
    enabled: !!selectedCity && cartState.items.length > 0
  });

  // Check for unavailable items and remove them
  useEffect(() => {
    if (productAvailability && Object.keys(productAvailability).length > 0) {
      const unavailableItemIds = cartState.items
        .filter(item => !productAvailability[item.id])
        .map(item => item.id);
      
      if (unavailableItemIds.length > 0) {
        removeUnavailableItems(unavailableItemIds);
      }
    }
  }, [productAvailability, cartState.items, removeUnavailableItems]);

  return null; // This component doesn't render anything
}
